import { useState, useEffect, useRef } from 'react';
import { signOut } from 'aws-amplify/auth';
import { userAppContext } from '../../context/Context';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SharedButton from '@/global/Button/Button';
import { UserCircleIcon, UserIcon } from '@heroicons/react/24/outline';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/solid';
import { useAuthStore } from '../../store/authStore';
import { NAVIGATION_LINKS } from './topNavigationHelpers';
import useText from '@/hooks/useText';
import { slugifyTrackingValue, trackEvent } from '@/utils/analytics';

const TopRightMenu = ({ renderizedBy }) => {
  const navigate = useNavigate();
  const labelAbout = useText('navigation.about');
  const labelTools = useText('navigation.tools');
  const labelAIDA = useText('navigation.aida');
  const labelAdvancedSearch = useText('navigation.advancedSearch');
  const labelEnrichment = useText('navigation.enrichment');

  const linkLabels: Record<string, string> = {
    'navigation.about': labelAbout,
    'navigation.tools': labelTools,
    'navigation.aida': labelAIDA,
    'navigation.advancedSearch': labelAdvancedSearch,
    'navigation.enrichment': labelEnrichment,
  };
  const { isAuthenticated, setIsAuthenticated, setGroups, setAuthorization } = userAppContext();
  const user = useAuthStore(state => state.user);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setUserDropdownOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const dropdownRef = useRef(null);
  const topRightMenuRef = useRef(null);
  const userDropdownRef = useRef(null);
  const location = useLocation();

  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  useEffect(() => {
    const calculateDropdownPosition = () => {
      if (topRightMenuRef.current && dropdownRef.current) {
        const topRightMenuRect = topRightMenuRef.current.getBoundingClientRect();
        const dropdownRect = dropdownRef.current.getBoundingClientRect();

        const top = topRightMenuRect.bottom + window.scrollY;
        const left = topRightMenuRect.left - (dropdownRect.width - topRightMenuRect.width);

        setDropdownPosition({ top, left });
      }
    };

    calculateDropdownPosition();

    window.addEventListener('resize', calculateDropdownPosition);

    return () => {
      window.removeEventListener('resize', calculateDropdownPosition);
    };
  }, [isDropdownOpen]);

  const handleLogout = () => {
    trackEvent({
      name: 'navigation_click',
      buttonId: 'navigation_logout',
      label: 'Logout',
      page: 'global',
      section: 'top_navigation',
      feature: 'navigation',
      eventCategory: 'navigation',
    });
    setDropdownOpen(false);
    setIsAuthenticated(false);
    setGroups([]);
    setAuthorization('');
    signOut();
    navigate('/');
  };

  const handleLabelClick = () => {
    setDropdownOpen(!isDropdownOpen);
  };
  const handleClickOutside = event => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target) &&
      topRightMenuRef.current &&
      !topRightMenuRef.current.contains(event.target)
    ) {
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleUserDropdownClick = () => {
    setUserDropdownOpen(prev => !prev);
  };

  const handleUserClickOutside = event => {
    if (
      userDropdownRef.current &&
      !userDropdownRef.current.contains(event.target) &&
      topRightMenuRef.current &&
      !topRightMenuRef.current.contains(event.target)
    ) {
      setUserDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleUserClickOutside);
    return () => document.removeEventListener('mousedown', handleUserClickOutside);
  }, []);

  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 992);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  const userDisplay = user?.email || user?.username || user?.sub || '';

  const closeMobileMenu = () => setDropdownOpen(false);

  const navList = () => (
    <>
      {isAuthenticated && !isDesktop && userDisplay && (
        <div className="nav-mobile-user-info">{userDisplay}</div>
      )}
      <ul className="flex flex-col lg:flex-row gap-4 md:gap-6 lg:gap-12 mx-4 md:mx-0 mt-4 mt-md-6 mt-lg-2 mb-md-2">
        {NAVIGATION_LINKS.filter(link => !link.showOnlyMobile || !isDesktop).map(link => (
          <li key={link.href} className="nav-link-item flex justify-start text-left">
            <Link
              to={link.href}
              onClick={() =>
                trackEvent({
                  name: 'navigation_click',
                  buttonId: `navigation_route_${slugifyTrackingValue(link.href)}`,
                  label: linkLabels[link.cmsKey] ?? link.label,
                  page: 'global',
                  section: 'top_navigation',
                  feature: 'navigation',
                  eventCategory: 'navigation',
                })
              }
            >
              {linkLabels[link.cmsKey] ?? link.label}
            </Link>
            {link.label === 'AIDA' && (
              <img
                src="../../static/images/eyesAida.svg"
                alt="bot-aida"
                width="22"
                height="22"
                className="ml-2"
              />
            )}
          </li>
        ))}
        {isAuthenticated && !isDesktop && (
          <li className="fixed bottom-0 left-0 right-0 mb-10 ml-6">
            <SharedButton variant="secondaryOutline" onClick={handleLogout}>
              Logout
            </SharedButton>
          </li>
        )}
      </ul>
    </>
  );

  const isApplyPage = renderizedBy === 'ApplyPage';
  const navIconClass = isApplyPage ? 'custom-nav-menu' : '';

  return (
    <div className="menuNav" ref={topRightMenuRef}>
      <nav
        className={`dropdown-menu ${isDropdownOpen ? 'open' : ''}`}
        style={location.pathname !== '/' ? { top: dropdownPosition.top } : {}}
        ref={dropdownRef}
      >
        <button
          type="button"
          className="nav-mobile-close-btn"
          onClick={() => {
            trackEvent({
              name: 'navigation_click',
              buttonId: 'navigation_close_mobile_menu',
              label: 'Close mobile menu',
              page: 'global',
              section: 'top_navigation',
              feature: 'navigation',
              eventCategory: 'navigation',
            });
            closeMobileMenu();
          }}
          aria-label="Close menu"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
        {navList()}
      </nav>
      {isAuthenticated ? (
        isDesktop ? (
          <div className="nav-user-dropdown-wrap" ref={userDropdownRef}>
            <button
              type="button"
              className="nav-user-dropdown-trigger"
              onClick={handleUserDropdownClick}
              aria-expanded={isUserDropdownOpen}
              aria-haspopup="true"
              aria-label="User menu"
            >
              <UserCircleIcon className={`nav-icon h-12 w-12 md:h-8 md:w-8 ${navIconClass}`} />
            </button>
            {isUserDropdownOpen && (
              <div className="nav-user-dropdown">
                {userDisplay && <div className="nav-user-dropdown-info">{userDisplay}</div>}
                <Link
                  to="/Profile-area"
                  className="nav-user-dropdown-item"
                  onClick={() => {
                    trackEvent({
                      name: 'navigation_click',
                      buttonId: 'navigation_route_profile_area',
                      label: 'Profile data',
                      page: 'global',
                      section: 'top_navigation',
                      feature: 'navigation',
                      eventCategory: 'navigation',
                    });
                    setUserDropdownOpen(false);
                  }}
                >
                  Profile Data
                </Link>
                <button
                  type="button"
                  className="nav-user-dropdown-item nav-user-dropdown-logout"
                  onClick={() => {
                    setUserDropdownOpen(false);
                    handleLogout();
                  }}
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        ) : null
      ) : (
        location.pathname !== '/login' && (
          <>
            <Link
              to="/login"
              onClick={() =>
                trackEvent({
                  name: 'navigation_click',
                  buttonId: 'navigation_route_login',
                  label: 'Login',
                  page: 'global',
                  section: 'top_navigation',
                  feature: 'navigation',
                  eventCategory: 'navigation',
                })
              }
            >
              <SharedButton id="login_button" variant="gradient" size="xs">
                <UserIcon className="h-2 w-2 mr-1 md:h-3 md:w-3" /> LOGIN
              </SharedButton>
            </Link>
          </>
        )
      )}
      <span
        className="hamburger"
        onClick={() => {
          trackEvent({
            name: 'navigation_click',
            buttonId: 'navigation_toggle_mobile_menu',
            label: 'Toggle mobile menu',
            page: 'global',
            section: 'top_navigation',
            feature: 'navigation',
            eventCategory: 'navigation',
          });
          handleLabelClick();
        }}
      >
        <Bars3Icon className={`h-8 w-8 md:h-10 md:w-10 nav-icon ${navIconClass}`} />
      </span>
    </div>
  );
};

export { TopRightMenu };
